import { faCheck, faPlus } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ProductOption } from "@kanpla/api-contract";
import { type AllergyMatcher } from "@kanpla/allergens";
import { clsx } from "clsx";

import { T } from "@repo/transifex";
import { QuantityInputControl } from "~/components/Inputs/quantity-input-control/quantity-input-control";
import { AllergensIcons } from "~/components/allergens";

type ChoiceProps = {
  choice: ProductOption["choices"][number];
  variantMaximum: number | null;
  checked: boolean;
  onItemClick: () => void;
  quantity: number;
  onQuantityIncrease: () => void;
  onQuantityDecrease: () => void;
  displayPrice: string | undefined;
  /** Formatted per-unit deposit of the choice's connected product. */
  displayDeposit?: string;
  matchAllergies: AllergyMatcher;
};

export const ChoiceInput = ({
  choice: {
    id,
    name,
    maximum: choiceMaximum,
    allergens: choiceAllergens,
    allergensDetailed
  },
  variantMaximum,
  checked,
  onItemClick,
  quantity,
  onQuantityIncrease,
  onQuantityDecrease,
  displayPrice,
  displayDeposit,
  matchAllergies
}: ChoiceProps) => {
  const conflict = matchAllergies(allergensDetailed);
  const matchingAllergens = conflict
    ? [...conflict.contains, ...conflict.mayContain]
    : [];
  const isAllergic = conflict !== null;
  const isBlocked = conflict?.action === "block";

  const isQuantityEnabled =
    checked &&
    (choiceMaximum === null || choiceMaximum > 1) &&
    (variantMaximum === null || variantMaximum > 1);

  return (
    <div
      id={id}
      className="flex w-full flex-row items-center justify-between border-b border-interface-neutral-3 py-2 text-start"
    >
      <button
        disabled={isBlocked}
        type="button"
        onClick={onItemClick}
        className="flex flex-1 items-center justify-between"
      >
        <div className="flex flex-col">
          <div className="flex flex-row items-center justify-between gap-2">
            <p
              className={clsx({
                "font-semibold": checked,
                "text-red-500": isAllergic
              })}
            >
              {name}
            </p>

            <AllergensIcons
              allergens={choiceAllergens}
              highlightAllergens={matchingAllergens}
            />
          </div>

          {displayPrice ? (
            <p className="text-start text-sm text-interface-neutral-5">
              {displayPrice}
            </p>
          ) : null}

          {displayDeposit ? (
            <p className="text-start text-sm text-interface-neutral-5">
              <T _str="+{deposit} Deposit Fee" deposit={displayDeposit} />
            </p>
          ) : null}
        </div>

        <div
          className={clsx(
            "flex aspect-square h-12 items-center justify-center gap-2 rounded-full",
            {
              hidden: (checked && isQuantityEnabled) || isBlocked,
              "bg-interface-neutral-3": !checked,
              "bg-interface-positive-2 text-interface-positive-1":
                checked && !isQuantityEnabled
            }
          )}
        >
          {checked ? (
            <FontAwesomeIcon icon={faCheck} />
          ) : (
            <FontAwesomeIcon icon={faPlus} />
          )}
        </div>

        {isAllergic ? (
          <span className="flex h-12 items-center justify-center font-normal text-red-500">
            <T _str="️Contains matching allergens" />
          </span>
        ) : null}
      </button>

      <div className="flex items-center gap-3">
        {isQuantityEnabled ? (
          <QuantityInputControl
            className="bg-interface-positive-2 text-interface-neutral-0"
            quantity={quantity}
            onIncrementClick={onQuantityIncrease}
            onDecrementClick={onQuantityDecrease}
            incrementDisabled={
              choiceMaximum ? choiceMaximum === quantity : false
            }
            variant="primary"
            size="sm"
          />
        ) : null}
      </div>
    </div>
  );
};
